import { useState, useMemo, useRef, useEffect, ChangeEvent, MouseEvent, ReactNode } from "react";
import { ConfirmModal, Product, SortDir, SortField, StatusFilter, ToastState } from "../../types/product";
import { PAGE_SIZE_OPTIONS, CATEGORIES } from "../../constants/product";
import { formatDate } from "../../helpers/product";
import { fetchAllProducts, deleteProduct, updateProductStatus } from "../../services/productService";
import { useNavigate } from "react-router-dom";
import BulkImportModal from "../../components/products/BulkImportModal";

// ─── Sub-components ───────────────────────────────────────────────────────────

function highlight(text: string, query: string): ReactNode {
    if (!query.trim()) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: "rgba(191,61,12,0.3)", color: "var(--or)", borderRadius: 2, padding: "0 1px" }}>
                {text.slice(idx, idx + query.length)}
            </mark>
            {text.slice(idx + query.length)}
        </>
    );
}

function StatusBadge({ status }: { status: Product["status"] }) {
    const map = {
        published: { bg: "rgba(109,220,109,0.10)", border: "rgba(109,220,109,0.25)", color: "#6ddc6d", dot: "#6ddc6d", label: "Published" },
        draft: { bg: "rgba(245,166,35,0.10)", border: "rgba(245,166,35,0.25)", color: "#f5a623", dot: "#f5a623", label: "Draft" },
        archived: { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.12)", color: "#6a6a65", dot: "#6a6a65", label: "Archived" },
    } as const;
    const s = map[status];
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.5px",
            background: s.bg, border: `1px solid ${s.border}`,
            color: s.color, borderRadius: 20, padding: "3px 8px",
        }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
            {s.label}
        </span>
    );
}

function CategoryPill({ cat }: { cat: string }) {
    return (
        <span style={{
            fontSize: 11, fontWeight: 500,
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
            color: "var(--text3)", borderRadius: 4, padding: "2px 7px",
        }}>{cat}</span>
    );
}

function IconBtn({ title, onClick, children, danger }: {
    title: string; onClick: () => void; children: ReactNode; danger?: boolean;
}) {
    const [hov, setHov] = useState(false);
    return (
        <button
            title={title} onClick={onClick}
            style={{
                width: 30, height: 30, borderRadius: 7,
                border: `1px solid ${hov ? (danger ? "rgba(224,82,82,0.35)" : "rgba(191,61,12,0.35)") : "var(--border)"}`,
                background: hov ? (danger ? "rgba(224,82,82,0.10)" : "var(--or-dim)") : "transparent",
                color: hov ? (danger ? "#e05252" : "var(--or)") : "var(--text3)",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s", flexShrink: 0,
            }}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
        >
            {children}
        </button>
    );
}

function SortIcon({ field, active, dir }: { field: string; active: boolean; dir: SortDir }) {
    return (
        <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, marginLeft: 4, opacity: active ? 1 : 0.3 }}>
            <span style={{ fontSize: 8, lineHeight: 1, color: active && dir === "asc" ? "var(--or)" : "var(--text3)" }}>▲</span>
            <span style={{ fontSize: 8, lineHeight: 1, color: active && dir === "desc" ? "var(--or)" : "var(--text3)" }}>▼</span>
        </span>
    );
}

function EmptyState({ query }: { query: string }) {
    return (
        <tr>
            <td colSpan={7} style={{ padding: "56px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>◫</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>
                    {query ? `No products match "${query}"` : "No products found"}
                </div>
                <div style={{ fontSize: 13, color: "var(--text3)" }}>
                    {query ? "Try a different search term or clear your filters." : "Add your first product to get started."}
                </div>
            </td>
        </tr>
    );
}

function DeleteModal({ modal, onConfirm, onCancel }: {
    modal: ConfirmModal; onConfirm: () => void; onCancel: () => void;
}) {
    if (!modal.open) return null;
    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 500,
            background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={onCancel}>
            <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{
                background: "var(--card-solid)", border: "1px solid var(--border-strong)",
                borderRadius: 16, padding: 28, width: 380, maxWidth: "90vw",
                animation: "modalIn 0.2s ease",
            }}>
                <div style={{
                    width: 44, height: 44, borderRadius: "50%", background: "rgba(224,82,82,0.12)",
                    border: "1px solid rgba(224,82,82,0.25)", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 20, marginBottom: 16,
                }}>🗑</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>Delete product?</div>
                <div style={{ fontSize: 14, color: "var(--text3)", lineHeight: 1.6, marginBottom: 24 }}>
                    <strong style={{ color: "var(--text2)" }}>{modal.productName}</strong> will be permanently deleted. This cannot be undone.
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={onCancel} style={{
                        padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)",
                        background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                    }}>Cancel</button>
                    <button onClick={onConfirm} style={{
                        padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(224,82,82,0.35)",
                        background: "rgba(224,82,82,0.12)", color: "#e05252", fontSize: 14,
                        fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                    }}>Delete</button>
                </div>
            </div>
        </div>
    );
}

function ViewModal({ product, onClose }: { product: Product | null; onClose: () => void }) {
    const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'applications' | 'seo'>('overview');

    // Reset active tab to 'overview' when the product changes/opens
    useEffect(() => {
        if (product) setActiveTab('overview');
    }, [product]);

    if (!product) return null;

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 500,
            background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }} onClick={onClose}>
            <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{
                background: "var(--card-solid)", border: "1px solid var(--border-strong)",
                borderRadius: 16, width: 680, maxWidth: "95vw", maxHeight: "85vh",
                overflow: "hidden", display: "flex", flexDirection: "column",
                animation: "modalIn 0.2s ease",
            }}>
                {/* Modal Header */}
                <div style={{
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    padding: "20px 24px 12px",
                }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <StatusBadge status={product.status} />
                            {product.ec && (
                                <span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 500 }}>
                                    {product.ec}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text)" }}>
                            {product.tab_name || "Product Details"}
                        </div>
                        {product.tab_sub && (
                            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
                                {product.tab_sub} • ID: #{product.id}
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} style={{
                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                        background: "transparent", color: "var(--text3)", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
                    }}>×</button>
                </div>

                {/* Tab Selector Bar */}
                <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--border)", padding: "0 24px 12px" }}>
                    {[
                        { id: "overview", label: "Overview" },
                        { id: "specs", label: `Specifications (${product.specs ? product.specs.length : 0})` },
                        { id: "applications", label: `Use Cases (${product.applications ? product.applications.length : 0})` },
                        { id: "seo", label: "SEO" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id as any)}
                            style={{
                                padding: "6px 12px", borderRadius: 6,
                                border: `1px solid ${activeTab === t.id ? "rgba(191,61,12,0.3)" : "transparent"}`,
                                background: activeTab === t.id ? "var(--or-dim)" : "transparent",
                                color: activeTab === t.id ? "var(--or)" : "var(--text3)",
                                fontSize: 12, fontWeight: 500, cursor: "pointer",
                                fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s"
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Scrollable Tab Content Panel */}
                <div style={{ overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
                    
                    {/* Tab 1: Overview */}
                    {activeTab === "overview" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 18, animation: "fadeSlideIn 0.2s ease" }}>
                            {product.tagline && (
                                <div style={{ 
                                    padding: "14px 18px", background: "var(--or-dim)", 
                                    borderLeft: "3px solid var(--or)", borderRadius: "0 8px 8px 0", 
                                    fontSize: 13, color: "var(--text2)", fontStyle: "italic", lineHeight: 1.5
                                }}>
                                    "{product.tagline}"
                                </div>
                            )}

                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>Description</div>
                                <div style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                                    {product.product_description}
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>Formats Available</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        {product.formats && product.formats.length > 0 ? (
                                            product.formats.map((f, i) => (
                                                <span key={i} style={{ fontSize: 11, background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", color: "var(--text2)", borderRadius: 4, padding: "3px 8px" }}>
                                                    {f}
                                                </span>
                                            ))
                                        ) : (
                                            <span style={{ fontSize: 12, color: "var(--text3)" }}>None defined</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>Certifications & Grades</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        {product.certs && product.certs.length > 0 ? (
                                            product.certs.map((c, i) => (
                                                <span key={i} style={{ fontSize: 11, background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", color: "var(--text2)", borderRadius: 4, padding: "3px 8px" }}>
                                                    {c}
                                                </span>
                                            ))
                                        ) : (
                                            <span style={{ fontSize: 12, color: "var(--text3)" }}>None defined</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {product.cta_copy && (
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>Formats CTA Copy</div>
                                    <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, background: "rgba(255,255,255,0.01)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px" }}>
                                        {product.cta_copy}
                                    </div>
                                </div>
                            )}

                            {product.tds_url && (
                                <div style={{ 
                                    display: "flex", justifyContent: "space-between", alignItems: "center", 
                                    background: "var(--input-bg)", border: "1px solid var(--border)", 
                                    borderRadius: 8, padding: "12px 16px", marginTop: 4
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 16 }}>📄</span>
                                        <span style={{ fontSize: 13, color: "var(--text2)", fontWeight: 500 }}>Technical Data Sheet (TDS)</span>
                                    </div>
                                    <a href={product.tds_url} target="_blank" rel="noopener noreferrer" style={{ 
                                        fontSize: 13, color: "var(--or)", fontWeight: 600, textDecoration: "none",
                                        display: "inline-flex", alignItems: "center", gap: 4
                                    }}>
                                        Open Document ↓
                                    </a>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tab 2: Specifications */}
                    {activeTab === "specs" && (
                        <div style={{ animation: "fadeSlideIn 0.2s ease" }}>
                            <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "var(--card)" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, textAlign: "left" }}>
                                    <thead>
                                        <tr style={{ background: "rgba(255,255,255,0.02)", borderBottom: "1px solid var(--border)" }}>
                                            <th style={{ padding: "10px 14px", color: "var(--text3)", fontWeight: 500 }}>Attribute Parameter</th>
                                            <th style={{ padding: "10px 14px", color: "var(--text3)", fontWeight: 500 }}>Target Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {product.specs && product.specs.length > 0 ? (
                                            product.specs.map((s, idx) => (
                                                <tr key={idx} style={{ borderBottom: idx < product.specs.length - 1 ? "1px solid var(--border)" : "none" }}>
                                                    <td style={{ padding: "12px 14px", color: "var(--text2)", fontWeight: 600 }}>{s.label}</td>
                                                    <td style={{ padding: "12px 14px", color: "var(--text3)" }}>{s.value}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={2} style={{ padding: "32px", textAlign: "center", color: "var(--text3)" }}>
                                                    No specifications defined.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Tab 3: Use Cases */}
                    {activeTab === "applications" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeSlideIn 0.2s ease" }}>
                            {product.applications && product.applications.length > 0 ? (
                                product.applications.map((app, i) => (
                                    <div key={i} style={{ 
                                        border: "1px solid var(--border)", borderRadius: 12, 
                                        padding: 18, display: "flex", flexDirection: "column", gap: 14,
                                        background: "rgba(255,255,255,0.01)"
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--or)", letterSpacing: "0.5px" }}>
                                                {app.kicker}
                                            </span>
                                            <span style={{ fontSize: 11, color: "var(--text3)" }}>Case #{i + 1}</span>
                                        </div>
                                        
                                        {app.heading && app.heading.length > 0 && (
                                            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
                                                {app.heading.filter(Boolean).join(" ")}
                                            </div>
                                        )}
                                        
                                        <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>{app.body}</div>
                                        
                                        {/* Metrics Grid */}
                                        {app.metrics && app.metrics.length > 0 && (
                                            <div>
                                                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Key Impact Metrics</div>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8 }}>
                                                    {app.metrics.map((m, idx) => (
                                                        <div key={idx} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                                                            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--or)" }}>{m.num}</div>
                                                            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>{m.label}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Transformation Before / After */}
                                        {((app.transformFrom && app.transformFrom.text) || (app.transformTo && app.transformTo.text)) && (
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                                {app.transformFrom && app.transformFrom.text && (
                                                    <div style={{ background: "rgba(224,82,82,0.03)", border: "1px solid rgba(224,82,82,0.12)", borderRadius: 8, padding: 10 }}>
                                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#e08b8b", textTransform: "uppercase", marginBottom: 4 }}>
                                                            {app.transformFrom.label || "Input"}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.4 }}>{app.transformFrom.text}</div>
                                                    </div>
                                                )}
                                                {app.transformTo && app.transformTo.text && (
                                                    <div style={{ background: "rgba(109,220,109,0.03)", border: "1px solid rgba(109,220,109,0.12)", borderRadius: 8, padding: 10 }}>
                                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#6ddc6d", textTransform: "uppercase", marginBottom: 4 }}>
                                                            {app.transformTo.label || "Output"}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.4 }}>{app.transformTo.text}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Buyers List */}
                                        {app.buyers && app.buyers.length > 0 && (
                                            <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                                                <span style={{ color: "var(--text3)", fontWeight: 500 }}>{app.buyersLabel || "Target Customers"}:</span>
                                                <span style={{ color: "var(--text2)", fontWeight: 600 }}>{app.buyers.join(", ")}</span>
                                            </div>
                                        )}

                                        {/* Flow Steps */}
                                        {app.flowSteps && app.flowSteps.length > 0 && (
                                            <div>
                                                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                                    {app.flowLabel || "Process Pathway Steps"}
                                                </div>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                    {app.flowSteps.map((step, idx) => (
                                                        <div key={idx} style={{ display: "flex", gap: 8, fontSize: 12, lineHeight: 1.5 }}>
                                                            <span style={{ color: "var(--or)", fontWeight: 700 }}>{idx + 1}.</span>
                                                            <div>
                                                                <strong style={{ color: "var(--text2)" }}>{step.title}</strong>
                                                                {step.desc && <span style={{ color: "var(--text3)" }}> — {step.desc}</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {app.flowResultText && (
                                            <div style={{ fontSize: 12, fontWeight: 600, color: "#6ddc6d", background: "rgba(109,220,109,0.06)", borderRadius: 6, padding: "8px 12px", border: "1px solid rgba(109,220,109,0.12)" }}>
                                                ✓ Outcome: {app.flowResultText}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div style={{ padding: "32px", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
                                    No applications or use cases defined.
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "seo" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 18, animation: "fadeSlideIn 0.2s ease" }}>
                            {/* SEO Metas */}
                            <div>
                                <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 4 }}>Meta Title</div>
                                <div style={{ fontSize: 13, color: "var(--text2)", background: "var(--input-bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px" }}>
                                    {product.meta_title || "—"}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>Meta Description</div>
                                <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, background: "var(--input-bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px" }}>
                                    {product.meta_description || "—"}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AllProducts() {
    const [products, setProducts]       = useState<Product[]>([]);
    const [loading, setLoading]         = useState(true);
    const [search, setSearch]           = useState("");
    const [statusFilter, setStatusFilter]     = useState<StatusFilter>("all");
    const [sortField, setSortField]           = useState<SortField>("updatedAt");
    const [sortDir, setSortDir]               = useState<SortDir>("desc");
    const [page, setPage]               = useState(1);
    const [pageSize, setPageSize]       = useState(10);
    const [selected, setSelected]       = useState<Set<number>>(new Set());
    const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ open: false, productId: null, productName: "" });
    const [viewProduct, setViewProduct] = useState<Product | null>(null);
    const [toast, setToast]             = useState<ToastState>({ visible: false, message: "", type: "success" });
    const [showImportModal, setShowImportModal] = useState(false);
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigate = useNavigate();

    // ── Fetch products from API ──────────────────────────────────────────────

    const refreshProducts = () => {
        setLoading(true);
        fetchAllProducts()
            .then((data) => setProducts(data))
            .catch(() => showToast("Failed to load products", "error"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchAllProducts()
            .then((data) => { if (!cancelled) setProducts(data); })
            .catch(() => { if (!cancelled) showToast("Failed to load products", "error"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // ── Toast helper ─────────────────────────────────────────────────────────

    const showToast = (message: string, type: ToastState["type"] = "success", navigateTo?: string) => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ visible: true, message, type });
        toastTimer.current = setTimeout(() => {
            setToast((t) => ({ ...t, visible: false }));
            if (navigateTo) navigate(navigateTo);
        }, 3000);
    };

    // ── Derived data ─────────────────────────────────────────────────────────

    const filtered = useMemo(() => {
        let list = [...products];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (p) => (p.tab_name || "").toLowerCase().includes(q) ||
                    String(p.id).toLowerCase().includes(q)
            );
        }
        if (statusFilter !== "all") list = list.filter((p) => p.status === statusFilter);

        list.sort((a, b) => {
            let va: string | number;
            let vb: string | number;
            if (sortField === "specs") {
                va = a.specs ? a.specs.length : 0;
                vb = b.specs ? b.specs.length : 0;
            } else {
                const valA = a[sortField];
                const valB = b[sortField];
                va = (typeof valA === "string" || typeof valA === "number") ? valA : "";
                vb = (typeof valB === "string" || typeof valB === "number") ? valB : "";
            }
            const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [products, search, statusFilter, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage   = Math.min(page, totalPages);
    const pageSlice  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

    // ── Selection ────────────────────────────────────────────────────────────

    const allOnPageSelected = pageSlice.length > 0 && pageSlice.every((p) => selected.has(p.id));
    const someSelected = selected.size > 0;

    const toggleSelectAll = () => {
        if (allOnPageSelected) {
            const next = new Set(selected);
            pageSlice.forEach((p) => next.delete(p.id));
            setSelected(next);
        } else {
            const next = new Set(selected);
            pageSlice.forEach((p) => next.add(p.id));
            setSelected(next);
        }
    };

    const toggleSelect = (id: number) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    // ── Actions ──────────────────────────────────────────────────────────────

    const toggleSort = (field: SortField) => {
        if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortField(field); setSortDir("asc"); }
    };

    const handleDeleteRequest = (id: number, name: string) =>
        setConfirmModal({ open: true, productId: id, productName: name });

    const handleDeleteConfirm = async () => {
        if (!confirmModal.productId) return;
        try {
            await deleteProduct(confirmModal.productId);
            setProducts((prev) => prev.filter((p) => p.id !== confirmModal.productId));
            setSelected((prev) => { const n = new Set(prev); n.delete(confirmModal.productId!); return n; });
            showToast("Product deleted successfully");
        } catch {
            showToast("Delete failed. Please try again.", "error");
        } finally {
            setConfirmModal({ open: false, productId: null, productName: "" });
        }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selected);
        try {
            await Promise.all(ids.map((id) => deleteProduct(id)));
            setProducts((prev) => prev.filter((p) => !selected.has(p.id)));
            showToast(`${ids.length} product${ids.length > 1 ? "s" : ""} deleted`);
            setSelected(new Set());
        } catch {
            showToast("Bulk delete failed. Please try again.", "error");
        }
    };

    const handleStatusChange = async (id: number, status: Product["status"]) => {
        try {
            await updateProductStatus(id, status);
            setProducts((prev) => prev.map((p) => p.id === id ? { ...p, status } : p));
            showToast(`Status updated to "${status}"`);
        } catch {
            showToast("Status update failed.", "error");
        }
    };

    // ── Pagination ───────────────────────────────────────────────────────────

    const pageNumbers = (): (number | "…")[] => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (safePage <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
        if (safePage >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
    };

    // ── Stats ────────────────────────────────────────────────────────────────

    const stats = useMemo(() => ({
        total:     products.length,
        published: products.filter((p) => p.status === "published").length,
        draft:     products.filter((p) => p.status === "draft").length,
        archived:  products.filter((p) => p.status === "archived").length,
    }), [products]);

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; font-family: 'DM Sans', sans-serif; }
        input::placeholder { color: var(--text3); }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
        @keyframes fadeSlideIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes toastIn     { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        @keyframes modalIn     { from { opacity:0; transform:scale(0.97); } to { opacity:1; transform:scale(1); } }
        .ap-row { transition: background 0.12s; }
        .ap-row:hover { background: var(--row-hover) !important; }
        .ap-th { user-select: none; }
        .ap-th:hover { color: var(--text) !important; }
        .ap-page-btn { transition: all 0.15s; }
        .ap-page-btn:hover:not(:disabled) { background: var(--or-dim) !important; color: var(--or) !important; border-color: rgba(191,61,12,0.3) !important; }
        .ap-filter-btn { transition: all 0.15s; }
        .ap-filter-btn:hover { background: rgba(255,255,255,0.05) !important; }
        @media (max-width: 768px) { .prod-stat { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .prod-stat { grid-template-columns: repeat(1, 1fr) !important; } .prod-page { display: block !important; }}
      `}</style>

            {/* Toast */}
            {toast.visible && (
                <div style={{
                    position: "fixed", top: 20, right: 20, zIndex: 600,
                    background: toast.type === "success" ? "#1a2e1a" : "#2e1a1a",
                    border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : "rgba(224,82,82,0.25)"}`,
                    borderRadius: 10, padding: "12px 18px",
                    display: "flex", alignItems: "center", gap: 10,
                    animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                }}>
                    <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : "#e08b8b" }}>
                        {toast.type === "success" ? "✓" : "✕"} &nbsp;{toast.message}
                    </span>
                </div>
            )}

            <DeleteModal modal={confirmModal} onConfirm={handleDeleteConfirm} onCancel={() => setConfirmModal({ open: false, productId: null, productName: "" })} />
            <ViewModal product={viewProduct} onClose={() => setViewProduct(null)} />
            <BulkImportModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} onSuccess={refreshProducts} />

            <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
                <div style={{ maxWidth: 1280, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

                    {/* ── Page header ── */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }} className="prod-page">
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>
                                Products
                            </div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>All Products</h1>
                            <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                                {loading ? "Loading…" : `${products.length} total products`}
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button style={{
                                display: "flex", alignItems: "center", gap: 7,
                                padding: "10px 16px", borderRadius: 9,
                                border: "1px solid var(--border)",
                                background: "rgba(255,255,255,0.03)",
                                color: "var(--text)", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                fontFamily: "'DM Sans', sans-serif",
                                marginTop: 6, transition: "all 0.15s",
                            }} 
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "var(--or-dim)";
                                e.currentTarget.style.borderColor = "rgba(191,61,12,0.35)";
                                e.currentTarget.style.color = "var(--or)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                                e.currentTarget.style.borderColor = "var(--border)";
                                e.currentTarget.style.color = "var(--text)";
                            }}
                            onClick={() => setShowImportModal(true)}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                Bulk Import
                            </button>
                            <button style={{
                                display: "flex", alignItems: "center", gap: 7,
                                padding: "10px 18px", borderRadius: 9,
                                border: "1px solid rgba(191,61,12,0.35)",
                                background: "linear-gradient(135deg, #BF3D0C, #E8593C)",
                                color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                fontFamily: "'DM Sans', sans-serif",
                                boxShadow: "0 2px 12px rgba(191,61,12,0.25)", marginTop: 6,
                            }} onClick={() => navigate("/manage/product")}>
                                <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Product
                            </button>
                        </div>
                    </div>

                    {/* ── Stat cards ── */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }} className="prod-stat">
                        {[
                            { label: "Total",     value: stats.total,     color: "var(--text2)", dot: "var(--text3)" },
                            { label: "Published", value: stats.published, color: "#6ddc6d",       dot: "#6ddc6d" },
                            { label: "Draft",     value: stats.draft,     color: "#f5a623",       dot: "#f5a623" },
                            { label: "Archived",  value: stats.archived,  color: "var(--text3)", dot: "var(--text3)" },
                        ].map((s) => (
                            <div key={s.label} style={{
                                background: "var(--card)", border: "1px solid var(--border)",
                                borderRadius: 12, padding: "14px 18px",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                            }}>
                                <div>
                                    <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: s.color, lineHeight: 1 }}>{loading ? "–" : s.value}</div>
                                </div>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, opacity: 0.6 }} />
                            </div>
                        ))}
                    </div>

                    {/* ── Toolbar + Table ── */}
                    <div style={{
                        background: "var(--card)", border: "1px solid var(--border)",
                        borderRadius: 14, overflow: "hidden",
                    }}>
                        {/* Toolbar */}
                        <div style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "14px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap",
                        }}>
                            {/* Search */}
                            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text3)", fontSize: 14, pointerEvents: "none" }}>⌕</span>
                                <input
                                    value={search}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                                    placeholder="Search by name or ID…"
                                    style={{
                                        width: "100%", background: "rgba(255,255,255,0.04)",
                                        border: "1px solid var(--border)", borderRadius: 8,
                                        padding: "8px 12px 8px 32px", fontSize: 13, color: "var(--text)",
                                        outline: "none", fontFamily: "'DM Sans', sans-serif",
                                    }}
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14 }}>×</button>
                                )}
                            </div>

                            {/* Status filter */}
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {(["all", "published", "draft", "archived"] as StatusFilter[]).map((s) => (
                                    <button key={s} className="ap-filter-btn" onClick={() => setStatusFilter(s)} style={{
                                        fontSize: 12, padding: "6px 12px", borderRadius: 7,
                                        border: `1px solid ${statusFilter === s ? "rgba(191,61,12,0.3)" : "var(--border)"}`,
                                        background: statusFilter === s ? "var(--or-dim)" : "transparent",
                                        color: statusFilter === s ? "var(--or)" : "var(--text3)",
                                        cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize",
                                    }}>{s === "all" ? "All status" : s}</button>
                                ))}
                            </div>


                            {someSelected && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                                    <span style={{ fontSize: 12, color: "var(--text3)" }}>{selected.size} selected</span>
                                    <button onClick={handleBulkDelete} style={{
                                        fontSize: 12, padding: "6px 12px", borderRadius: 7,
                                        border: "1px solid rgba(224,82,82,0.3)", background: "rgba(224,82,82,0.08)",
                                        color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                                    }}>Delete selected</button>
                                </div>
                            )}

                            <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: someSelected ? 0 : "auto", whiteSpace: "nowrap" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        </div>

                        {/* Table */}
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                                <thead>
                                    <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                                        <th style={{ width: 44, padding: "11px 16px", textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                                            <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                                        </th>
                                        {([
                                            { field: "tab_name" as SortField, label: "Product", width: "auto" },
                                            { field: "status"       as SortField, label: "Status",   width: 120 },
                                            { field: "specs"        as SortField, label: "Specs",    width: 72 },
                                            { field: "updatedAt"    as SortField, label: "Updated",  width: 110 },
                                        ] as { field: SortField; label: string; width: number | string }[]).map((col) => (
                                            <th key={col.field} className="ap-th" onClick={() => toggleSort(col.field)} style={{
                                                width: col.width, padding: "11px 14px", textAlign: "left",
                                                borderBottom: "1px solid var(--border)",
                                                fontSize: 11, fontWeight: 600, color: sortField === col.field ? "var(--or)" : "var(--text3)",
                                                textTransform: "uppercase", letterSpacing: "0.8px", cursor: "pointer", whiteSpace: "nowrap",
                                            }}>
                                                {col.label}
                                                <SortIcon field={col.field} active={sortField === col.field} dir={sortDir} />
                                            </th>
                                        ))}
                                        <th style={{ width: 110, padding: "11px 14px", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} style={{ padding: "48px 24px", textAlign: "center", color: "var(--text3)", fontSize: 14 }}>Loading products…</td>
                                        </tr>
                                    ) : pageSlice.length === 0 ? (
                                        <EmptyState query={search} />
                                    ) : (
                                        pageSlice.map((product, idx) => (
                                            <tr key={product.id} className="ap-row" style={{
                                                background: selected.has(product.id) ? "rgba(191,61,12,0.06)" : "transparent",
                                                borderBottom: idx < pageSlice.length - 1 ? "1px solid var(--border)" : "none",
                                                animation: `fadeSlideIn 0.2s ease ${idx * 0.025}s both`,
                                            }}>
                                                <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                    <input type="checkbox" checked={selected.has(product.id)} onChange={() => toggleSelect(product.id)} style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                                                </td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 2 }}>
                                                        {highlight(product.tab_name || "", search)}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "monospace" }}>#{product.id}</div>
                                                </td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <div style={{ position: "relative", display: "inline-block" }}>
                                                        <StatusBadge status={product.status} />
                                                        <select
                                                            value={product.status}
                                                            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleStatusChange(product.id, e.target.value as Product["status"])}
                                                            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                                                        >
                                                            <option value="published">Published</option>
                                                            <option value="draft">Draft</option>
                                                            <option value="archived">Archived</option>
                                                        </select>
                                                    </div>
                                                </td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <span style={{ fontSize: 14, color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>{product.specs ? product.specs.length : 0}</span>
                                                </td>
                                                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text3)", whiteSpace: "nowrap" }}>
                                                    {formatDate(product.updatedAt)}
                                                </td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <div style={{ display: "flex", gap: 5 }}>
                                                        <IconBtn title="View" onClick={() => setViewProduct(product)}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Edit" onClick={() => navigate("/manage/product", { state: { id: product.id } })}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Delete" danger onClick={() => handleDeleteRequest(product.id, product.tab_name)}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
                                                            </svg>
                                                        </IconBtn>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 12, color: "var(--text3)" }}>Rows per page</span>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{
                                    fontSize: 12, padding: "4px 8px", borderRadius: 6,
                                    border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)",
                                    color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", outline: "none",
                                }}>
                                    {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n} style={{ background: "#1a1917" }}>{n}</option>)}
                                </select>
                            </div>
                            <span style={{ fontSize: 12, color: "var(--text3)" }}>
                                {filtered.length === 0 ? "0" : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                {[
                                    { label: "«", action: () => setPage(1), disabled: safePage === 1 },
                                    { label: "‹", action: () => setPage((p) => Math.max(1, p - 1)), disabled: safePage === 1 },
                                ].map(({ label, action, disabled }) => (
                                    <button key={label} className="ap-page-btn" onClick={action} disabled={disabled} style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                                        background: "transparent", color: disabled ? "var(--text3)" : "var(--text2)",
                                        cursor: disabled ? "not-allowed" : "pointer", fontSize: 14,
                                        opacity: disabled ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>{label}</button>
                                ))}
                                {pageNumbers().map((n, i) =>
                                    n === "…" ? (
                                        <span key={`e-${i}`} style={{ width: 30, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>…</span>
                                    ) : (
                                        <button key={n} className="ap-page-btn" onClick={() => setPage(n as number)} style={{
                                            width: 30, height: 30, borderRadius: 7,
                                            border: `1px solid ${safePage === n ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
                                            background: safePage === n ? "var(--or-dim)" : "transparent",
                                            color: safePage === n ? "var(--or)" : "var(--text2)",
                                            cursor: "pointer", fontSize: 13, fontWeight: safePage === n ? 600 : 400,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>{n}</button>
                                    )
                                )}
                                {[
                                    { label: "›", action: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: safePage === totalPages },
                                    { label: "»", action: () => setPage(totalPages), disabled: safePage === totalPages },
                                ].map(({ label, action, disabled }) => (
                                    <button key={label} className="ap-page-btn" onClick={action} disabled={disabled} style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                                        background: "transparent", color: disabled ? "var(--text3)" : "var(--text2)",
                                        cursor: disabled ? "not-allowed" : "pointer", fontSize: 14,
                                        opacity: disabled ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>{label}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
